import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useCategory } from '../../../hooks/useCategories';
import { useDeleteSubcategory } from '../../../hooks/useSubcategories';
import {
  Box,
  Button,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Spinner,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Input,
  useToast,
  HStack,
} from '@chakra-ui/react';
import Api from '../../../api/ApiUrl';
import { Link, useNavigate, useParams } from 'react-router-dom';
import SubcategoryForm from '../Subcategory/SubcategoryForm';
import { getSubcategories, deleteSubcategory } from '../../../api/subcategory';

const limit = 50;

const CategoryDetails = () => {
  const { id: categoryId } = useParams();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isUpdateOpen, onOpen: onUpdateOpen, onClose: onUpdateClose } = useDisclosure();
  const [updateId, setUpdateId] = useState(null);
  const [search, setSearch] = useState('');
  const [params, setParams] = useState({ page: 1, limit });
  const { data, error, isLoading } = useCategory(categoryId);
  const [filteredSubcategories, setFilteredSubcategories] = useState([]);
  const toast = useToast();
  const queryClient = useQueryClient();

  const deleteMutation = useMutation(deleteSubcategory, {
    onSuccess: () => {
      queryClient.invalidateQueries('subcategories');
      toast({
        title: 'Success',
        description: 'Auctions deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onClose();
      window.location.reload();
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to delete Auctions',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    },
  });

  useEffect(() => {
    if (data?.data?.data?.subcategoryCount) {
      setFilteredSubcategories(data.data.data.subcategoryCount);
    }
  }, [data]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    if (e.target.value) {
      setFilteredSubcategories(
        data.data.data.subcategoryCount.filter((sub) =>
          sub.name.toLowerCase().includes(e.target.value.toLowerCase())
        )
      );
    } else {
      setFilteredSubcategories(data.data.data.subcategoryCount);
    }
  };

  const handlePageChange = (newPage) => {
    setParams({ ...params, page: newPage });
  };

  const handleDelete = (subcategoryId) => {
    deleteMutation.mutate(subcategoryId);
  };

  // const handleCopyURL = (subcategoryId) => {
  //   const url = `${window.location.origin}/auction/${subcategoryId}`;
  //   navigator?.clipboard?.writeText(url);
  //   toast({
  //     title: 'URL Copied',
  //     description: 'The auction URL has been copied to your clipboard.',
  //     status: 'info',
  //     duration: 3000,
  //     isClosable: true,
  //   });
  // };

  // const handleCopyURL = (subcategoryId) => {
  //   const url = `${window.location.origin}/auction/${subcategoryId}`;
  //   if (navigator?.clipboard?.writeText) {
  //     navigator.clipboard.writeText(url).then(() => {
  //       toast({
  //         title: 'URL Copied',
  //         description: 'The auction URL has been copied to your clipboard.',
  //         status: 'info',
  //         duration: 3000,
  //         isClosable: true,
  //       });
  //     }).catch(err => {
  //       console.error('Failed to copy URL: ', err);
  //       toast({
  //         title: 'Error',
  //         description: 'Failed to copy the URL.',
  //         status: 'error',
  //         duration: 3000,
  //         isClosable: true,
  //       });
  //     });
  //   } else {
  //     console.error('Clipboard API not supported.');
  //     toast({
  //       title: 'Error',
  //       description: 'Clipboard API not supported in your browser.',
  //       status: 'error',
  //       duration: 3000,
  //       isClosable: true,
  //     });
  //   }
  // };

  const handleCopyURL = (subcategoryId) => {
    const url = `${window.location.origin}/auction/${subcategoryId}`;

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        toast({
          title: 'URL Copied',
          description: 'The auction URL has been copied to your clipboard.',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }).catch(err => {
        console.error('Failed to copy URL: ', err);
        toast({
          title: 'Error',
          description: 'Failed to copy the URL.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      });
    } else {
      const input = document.createElement('input');
      input.value = url;
      document.body.appendChild(input);
      input.select();
      input.setSelectionRange(0, 99999); // For mobile devices

      try {
        document.execCommand('copy');
        toast({
          title: 'URL Copied',
          description: 'The auction URL has been copied to your clipboard.',
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      } catch (err) {
        console.error('Failed to copy URL: ', err);
        toast({
          title: 'Error',
          description: 'Failed to copy the URL.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }

      document.body.removeChild(input);
    }
  };
  if (isLoading) return <Spinner />;
  if (error) return <div>Error fetching category details</div>;

  const currentPageSubcategories = filteredSubcategories
  ?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))  // Sort by newest first
  .slice((params.page - 1) * params.limit, params.page * params.limit);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" mb="4">
        <Button onClick={() => navigate('/dashboard/admin/categories')}>Back to Categories</Button>
        <Button colorScheme="teal" onClick={() => { setUpdateId(null); onOpen(); }}>Add Auctions</Button>
      </Box>
      <Box>
        <h2>{data?.data?.data?.name}</h2>
        <h2>Auctions count: {data?.data?.data?.subcategoryCount?.length}</h2>
      </Box>
      <Box display="flex" gap="2" mt="4">
        <Input placeholder="Search subcategories..." value={search} onChange={handleSearchChange} />
      </Box>
      <Table mt="4">
        <Thead>
          <Tr>
            <Th>Name</Th>
            <Th>Description</Th>
            <Th>Lots Count</Th>
            <Th>Selected to Slider</Th>
            <Th>Image Cover</Th>
            <Th>Start Date</Th>
            <Th>End Date</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {currentPageSubcategories.map((subcategory) => (
            <Tr key={subcategory._id}>
              <Td>{subcategory.name}</Td>
              <Td>{subcategory.description}</Td>
              <Td>{subcategory.items.length}</Td>
              <Td>{subcategory.seletedtoslider ? 'Yes' : 'No'}</Td>
              <Td>
                <img src={`${Api}/${subcategory?.imagecover?.path}`} alt={subcategory.imagecover.name} width="50" />
              </Td>
              <Td>{new Date(subcategory.startDate).toLocaleString()}</Td>
              <Td>{new Date(subcategory.endDate).toLocaleString()}</Td>
              <Td>
                <HStack spacing={2}>
                  <Button size="sm" onClick={() => navigate(`/dashboard/admin/subcategories/${subcategory._id}`)}>View</Button>
                  <Button size="sm" onClick={() => { setUpdateId(subcategory._id); onUpdateOpen(); }}>Update</Button>
                  <Button size="sm" colorScheme="red" onClick={() => handleDelete(subcategory._id)}>Delete</Button>
                  <Button size="sm" colorScheme="blue" onClick={() => handleCopyURL(subcategory._id)}>Copy URL</Button>
                  <Button size="sm" colorScheme="green" as={Link} to={`/auction/${subcategory._id}`}>Watch Auction</Button>
                </HStack>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
      <Box mt="4" display="flex" justifyContent="space-between">
        <Button onClick={() => handlePageChange(params.page - 1)} disabled={params.page === 1}>Previous</Button>
        <Button onClick={() => handlePageChange(params.page + 1)} disabled={currentPageSubcategories.length < limit}>Next</Button>
      </Box>
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Auctions</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SubcategoryForm categoryId={categoryId} />
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={isUpdateOpen} onClose={onUpdateClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Update Auctions</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SubcategoryForm subcategoryId={updateId} categoryId={categoryId} />
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onUpdateClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CategoryDetails;


// import React, { useState, useEffect } from 'react';
// import { useQuery, useMutation, useQueryClient } from 'react-query';
// import { useCategory, useUpdateCategory, useDeleteCategory } from '../../../hooks/useCategories';
// import { useDeleteSubcategory, useUpdateSubcategory, useSubcategories } from '../../../hooks/useSubcategories';
// import {
//   Box,
//   Button,
//   Table,
//   Tbody,
//   Td,
//   Th,
//   Thead,
//   Tr,
//   Spinner,
//   useDisclosure,
//   Modal,
//   ModalOverlay,
//   ModalContent,
//   ModalHeader,
//   ModalFooter,
//   ModalBody,
//   ModalCloseButton,
//   Input,
//   useToast,
// } from '@chakra-ui/react';
// import Api from '../../../api/ApiUrl'

// import { Link, useNavigate, useParams } from 'react-router-dom';
// import SubcategoryForm from '../Subcategory/SubcategoryForm';
// import { getSubcategories, deleteSubcategory } from '../../../api/subcategory';


// const limit = 2;

// const CategoryDetails = () => {
//   const { id: categoryId } = useParams();
//   const navigate = useNavigate();
//   const { isOpen, onOpen, onClose } = useDisclosure();
//   const { isOpen: isUpdateOpen, onOpen: onUpdateOpen, onClose: onUpdateClose } = useDisclosure();
//   const [updateId, setUpdateId] = useState(null);
//   const [search, setSearch] = useState('');
//   const [params, setParams] = useState({ page: 1, limit });
//   const { data, error, isLoading } = useCategory(categoryId);
//   const [filteredSubcategories, setFilteredSubcategories] = useState([]);
//   const toast = useToast();
//   // const deleteSubcategory = useDeleteSubcategory();
//   const queryClient = useQueryClient();
  
//   const deleteMutation = useMutation(deleteSubcategory, {
//     onSuccess: () => {
//       queryClient.invalidateQueries('subcategories');
//       toast({
//         title: 'Success',
//         description: 'Auctions deleted successfully',
//         status: 'success',
//         duration: 3000,
//         isClosable: true,
//       });
//       onClose();
//       window.location.reload();
//     },
//     onError: (error) => {
//       toast({
//         title: 'Error',
//         description: error.response?.data?.message || 'Failed to delete Auctions',
//         status: 'error',
//         duration: 3000,
//         isClosable: true,
//       });
//     },
//   });
//   useEffect(() => {
//     if (data?.data?.data?.subcategoryCount) {
//       setFilteredSubcategories(data.data.data.subcategoryCount);
//     }
//   }, [data]);

//   const handleSearchChange = (e) => {
//     setSearch(e.target.value);
//     if (e.target.value) {
//       setFilteredSubcategories(
//         data.data.data.subcategoryCount.filter((sub) =>
//           sub.name.toLowerCase().includes(e.target.value.toLowerCase())
//         )
//       );
//     } else {
//       setFilteredSubcategories(data.data.data.subcategoryCount);
//     }
//   };

//   const handlePageChange = (newPage) => {
//     setParams({ ...params, page: newPage });
//   };

//   if (isLoading) return <Spinner />;
//   if (error) return <div>Error fetching category details</div>;

//   const currentPageSubcategories = filteredSubcategories?.slice(
//     (params.page - 1) * params.limit,
//     params.page * params.limit
//   );

//   const handleDelete = (subcategoryId) => {
//     console.log(subcategoryId)
//     deleteMutation.mutate(subcategoryId);
//   };
//   const handleCopyURL = (subcategoryId) => {
//     const url = `${window.location.origin}/auction/${subcategoryId}`;
//     navigator.clipboard.writeText(url);
//     toast({
//       title: 'URL Copied',
//       description: 'The auction URL has been copied to your clipboard.',
//       status: 'info',
//       duration: 3000,
//       isClosable: true,
//     });
//   };
//   return (
//     <Box>
//       <Box display="flex" justifyContent="space-between" mb="4">
//         <Button onClick={() => navigate('/dashboard/admin/categories')}>Back to Categories</Button>
//         <Button colorScheme="teal" onClick={() => { setUpdateId(null); onOpen(); }}>Add Auctions</Button>
//       </Box>
//       <Box>
//         <h2>{data?.data?.data?.name}</h2>
//         <h2>Auctions count: {data?.data?.data?.subcategoryCount?.length}</h2>
//       </Box>
//       <Box display="flex" gap="2" mt="4">
//         <Input placeholder="Search subcategories..." value={search} onChange={handleSearchChange} />
//       </Box>
//       <Table mt="4">
//         <Thead>
//           <Tr>
//             <Th>Name</Th>
//             <Th>Description</Th>
//             <Th>Lots Count</Th>
//             <Th>Selected to Slider</Th>
//             <Th>Image Cover</Th>
//             <Th>startDate</Th>
//             <Th>endDate</Th>
//             <Th>Actions</Th>

//           </Tr>
//         </Thead>
//         <Tbody>
//           {currentPageSubcategories.map((subcategory) => (
//             <Tr key={subcategory._id}>
//               <Td>{subcategory.name}</Td>
//               <Td>{subcategory.description}</Td>
//               <Td>{subcategory.items.length}</Td>
//               <Td>{subcategory.seletedtoslider ? 'Yes' : 'No'}</Td>
//               <Td>
//                 <img src={`${Api}/${subcategory?.imagecover?.path}`} alt={subcategory.imagecover.name} width="50" />
//               </Td>
//               <Td>{new Date(subcategory.startDate).toLocaleString()}</Td>
//               <Td>{new Date(subcategory.endDate).toLocaleString()}</Td>


//               <Td>
//         <Button size="sm" onClick={() => navigate(`/dashboard/admin/subcategories/${subcategory._id}`)}>View</Button>

//                 <Button size="sm" onClick={() => { setUpdateId(subcategory._id); onUpdateOpen(); }}>Update</Button>
//                 <Button size="sm" colorScheme="red" onClick={() => handleDelete(subcategory._id)}>Delete</Button>
//                 <Button colorScheme="blue" onClick={() => handleCopyURL(subcategory?._id)}>
//                   Copy URL
//                 </Button>
//                 <Button colorScheme="green" as={Link} to={`/auction/${subcategory?.subcategory?._id}`}>
//                   Watch Auction
//                 </Button>
//               </Td>
//             </Tr>
//           ))}
//         </Tbody>
//       </Table>
//       <Box mt="4" display="flex" justifyContent="space-between">
//         <Button onClick={() => handlePageChange(params.page - 1)} disabled={params.page === 1}>Previous</Button>
//         <Button onClick={() => handlePageChange(params.page + 1)} disabled={currentPageSubcategories.length < limit}>Next</Button>
//       </Box>
//       <Modal isOpen={isOpen} onClose={onClose}>
//         <ModalOverlay />
//         <ModalContent>
//           <ModalHeader>Add Auctions</ModalHeader>
//           <ModalCloseButton />
//           <ModalBody>
//             <SubcategoryForm categoryId={categoryId} />
//           </ModalBody>
//           <ModalFooter>
//             <Button colorScheme="blue" onClick={onClose}>Close</Button>
//           </ModalFooter>
//         </ModalContent>
//       </Modal>
//       <Modal isOpen={isUpdateOpen} onClose={onUpdateClose}>
//         <ModalOverlay />
//         <ModalContent>
//           <ModalHeader>Update Auctions</ModalHeader>
//           <ModalCloseButton />
//           <ModalBody>
//             <SubcategoryForm subcategoryId={updateId} categoryId={categoryId} />
//           </ModalBody>
//           <ModalFooter>
//             <Button colorScheme="blue" onClick={onUpdateClose}>Close</Button>
//           </ModalFooter>
//         </ModalContent>
//       </Modal>
//     </Box>
//   );
// };

// export default CategoryDetails;


// import React, { useState, useEffect } from 'react';
// import { useCategory} from '../../../hooks/useCategories';
// import { useDeleteSubcategory, useUpdateSubcategory,useSubcategories } from '../../../hooks/useSubcategories';
// import {
//   Box,
//   Button,
//   Table,
//   Tbody,
//   Td,
//   Th,
//   Thead,
//   Tr,
//   Spinner,
//   useDisclosure,
//   Alert,
//   AlertDialog,
//   AlertDialogBody,
//   AlertDialogFooter,
//   AlertDialogHeader,
//   AlertDialogContent,
//   AlertDialogOverlay,
//   useToast,
//   Input,
//   Modal,
//   ModalOverlay,
//   ModalContent,
//   ModalHeader,
//   ModalFooter,
//   ModalBody,
//   ModalCloseButton,
// } from '@chakra-ui/react';
// import { useNavigate, useParams } from 'react-router-dom';
// import SubcategoryForm from '../Subcategory/SubcategoryForm';
// import queryString from 'query-string';

// const limit = 2;

// const CategoryDetails = () => {
//   const { id } = useParams();
//   const navigate = useNavigate();
//   const { isOpen, onOpen, onClose } = useDisclosure();
//   const { isOpen: isUpdateOpen, onOpen: onUpdateOpen, onClose: onUpdateClose } = useDisclosure();
//   const [updateId, setUpdateId] = useState(null);
//   const [search, setSearch] = useState('');
//   const [params, setParams] = useState({ page: 1, limit, sort: '' });
//   const { data, error, isLoading } = useCategory(id);
//   console.log(data)
//   const { isLoading: subcategoriesLoading, error: subcategoriesError, data: subcategoriesFetching} = useSubcategories({ ...params, categoryId: id });
//   console.log(subcategoriesFetching)
//   const deleteSubcategory = useDeleteSubcategory();
//   const updateSubcategory = useUpdateSubcategory();
//   const toast = useToast();

//   useEffect(() => {
//     const queryParams = queryString.parse(window.location.search);
//     setParams({
//       ...params,
//       ...queryParams,
//       page: queryParams.page ? Number(queryParams.page) : 1,
//     });
//   }, [window.location.search]);

//   const handleDelete = (subcategoryId) => {
//     deleteSubcategory.mutate(subcategoryId, {
//       onSuccess: () => {
//         toast({
//           title: 'Subcategory deleted',
//           status: 'success',
//           duration: 3000,
//           isClosable: true,
//         });
//       },
//     });
//   };

//   const handleSearchChange = (e) => {
//     setSearch(e.target.value);
//   };

//   const handleSearchClick = () => {
//     setParams({ ...params, name: search, page: 1 });
//   };

//   const handleUpdate = (subcategory) => {
//     updateSubcategory.mutate(subcategory, {
//       onSuccess: () => {
//         toast({
//           title: 'Subcategory updated',
//           status: 'success',
//           duration: 3000,
//           isClosable: true,
//         });
//       },
//     });
//   };

//   const handlePageChange = (newPage) => {
//     setParams({ ...params, page: newPage });
//   };

//   if (isLoading) return <Spinner />;
//   if (error) return <div>Error fetching category details</div>;

//   if (subcategoriesLoading) return <Spinner />;
//   if (subcategoriesError) return <div>Error fetching category details</div>;

//   return (
//     <Box>
//       <Box display="flex" justifyContent="space-between" mb="4">
//         <Button onClick={() => navigate('/dashboard/admin/categories')}>Back to Categories</Button>
//         <Button colorScheme="teal" onClick={onOpen}>Add Subcategory</Button>
//       </Box>
//       <Box>
//         <h2>{data?.data?.data?.name}</h2>

//         {/* <p>Cover: {data?.data?.data?.cover.name}</p> */}
//         {/* Display more category details as needed */}
//         <h2>subcategory count: {data?.data?.data?.subcategoryCount?.length}</h2>
//       </Box>
//       <Box display="flex" gap="2" mt="4">
//         <Input placeholder="Search subcategories..." value={search} onChange={handleSearchChange} />
//         <Button onClick={handleSearchClick} colorScheme="blue">Search</Button>
//       </Box>
//       <Table mt="4">
//         <Thead>
//           <Tr>
//             <Th>Name</Th>
//             <Th>Description</Th>
//             <Th>Items Count</Th>
//             <Th>Selected to Slider</Th>
//             <Th>Image Cover</Th>
//             <Th>Actions</Th>
//           </Tr>
//         </Thead>
//         <Tbody>
//           {subcategoriesFetching.data?.map((subcategory) => (
//             <Tr key={subcategory._id}>
//               <Td>{subcategory?.subcategory?.name}</Td>
//               <Td>{subcategory?.subcategory?.description}</Td>
//               <Td>{subcategory?.subcategory?.items.length}</Td>
//               <Td>{subcategory?.subcategory?.seletedtoslider ? 'Yes' : 'No'}</Td>
//               {/* <Td>{subcategory.imagecover.name}</Td> */}
//               <Td>
//                 <Button size="sm" onClick={() => navigate(`/subcategories/${subcategory._id}`)}>View</Button>
//                 <Button size="sm" onClick={() => { setUpdateId(subcategory._id); onUpdateOpen(); }}>Update</Button>
//                 <Button size="sm" colorScheme="red" onClick={() => handleDelete(subcategory._id)}>Delete</Button>
//               </Td>
//             </Tr>
//           ))}
//         </Tbody>
//       </Table>
//       <Box mt="4" display="flex" justifyContent="space-between">
//         <Button onClick={() => handlePageChange(params.page - 1)} disabled={params.page === 1}>Previous</Button>
//         <Button onClick={() => handlePageChange(params.page + 1)} disabled={!subcategoriesFetching.data.hasMore}>Next</Button>
//       </Box>
//       <Modal isOpen={isOpen} onClose={onClose}>
//         <ModalOverlay />
//         <ModalContent>
//           <ModalHeader>Add Subcategory</ModalHeader>
//           <ModalCloseButton />
//           <ModalBody>
//             <SubcategoryForm initialValues={{ name: '', description: '', itemscount: '', seletedtoslider: false, imagecover: { file: null } }} />
//           </ModalBody>
//           <ModalFooter>
//             <Button colorScheme="blue" onClick={onClose}>Close</Button>
//           </ModalFooter>
//         </ModalContent>
//       </Modal>
//       <Modal isOpen={isUpdateOpen} onClose={onUpdateClose}>
//         <ModalOverlay />
//         <ModalContent>
//           <ModalHeader>Update Subcategory</ModalHeader>
//           <ModalCloseButton />
//           <ModalBody>
//             <SubcategoryForm initialValues={subcategoriesFetching.data?.data?.find(sc => sc._id === updateId)} />
//           </ModalBody>
//           <ModalFooter>
//             <Button colorScheme="blue" onClick={onUpdateClose}>Close</Button>
//           </ModalFooter>
//         </ModalContent>
//       </Modal>
//     </Box>
//   );
// };

// export default CategoryDetails;





